import type { CampaignFormData } from '../types';
import SAR from '@/components/feature/SAR';

interface Step4Props {
  data: CampaignFormData;
  onEdit: (step: number) => void;
  agreed: boolean;
  onAgreeChange: (val: boolean) => void;
}

const Step4Review = ({ data, onEdit, agreed, onAgreeChange }: Step4Props) => {
  const goal = Number(data.goalAmount) || 0;
  const formatNum = (n: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

  const sections = [
    {
      step: 1,
      title: 'المعلومات الأساسية',
      icon: 'ri-file-text-line',
      items: [
        { label: 'عنوان الحملة', value: data.title || '—' },
        { label: 'الفئة', value: data.category || '—' },
        { label: 'المدينة', value: data.location || '—' },
        { label: 'الوصف المختصر', value: data.shortDesc || '—' },
      ],
    },
    {
      step: 2,
      title: 'تفاصيل الحملة',
      icon: 'ri-article-line',
      items: [
        { label: 'القصة', value: data.story ? data.story.slice(0, 100) + (data.story.length > 100 ? '...' : '') : '—' },
        { label: 'الوسوم', value: data.tags || '—' },
      ],
    },
    {
      step: 3,
      title: 'الهدف المالي',
      icon: 'ri-money-dollar-circle-line',
      items: [
        { label: 'المبلغ المستهدف', value: goal > 0 ? <SAR amount={goal} iconSize={12} /> : '—' },
        { label: 'رسوم المنصة (5%)', value: goal > 0 ? <><span>- </span><SAR amount={goal * 0.05} iconSize={12} /></> : '—' },
        { label: 'ستستلم تقريباً', value: goal > 0 ? <SAR amount={goal * 0.95} iconSize={12} /> : '—' },
        { label: 'مدة الحملة', value: data.duration ? `${data.duration} يوم` : '—' },
        { label: 'رقم الآيبان', value: data.iban ? `****${data.iban.slice(-4)}` : '—' },
      ],
    },
  ];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h2 className="text-xl font-black text-stone-800 mb-1">مراجعة ونشر الحملة</h2>
        <p className="text-stone-500 text-sm">تحقق من جميع المعلومات قبل الإرسال للمراجعة</p>
      </div>

      {/* Cover Preview */}
      {data.coverImageUrl && (
        <div className="rounded-xl overflow-hidden h-40">
          <img
            src={data.coverImageUrl}
            alt="غلاف الحملة"
            className="w-full h-full object-cover object-top"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}

      {/* Review Sections */}
      {sections.map((section) => (
        <div key={section.step} className="bg-stone-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg">
                <i className={`${section.icon} text-emerald-600 text-sm`}></i>
              </div>
              <span className="font-bold text-stone-800 text-sm">{section.title}</span>
            </div>
            <button
              type="button"
              onClick={() => onEdit(section.step)}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1"
            >
              <i className="ri-edit-line text-xs"></i>
              تعديل
            </button>
          </div>
          <div className="space-y-2">
            {section.items.map((item) => {
              const isFee = item.label === 'رسوم المنصة (5%)';
              const isNet = item.label === 'ستستلم تقريباً';
              return (
                <div
                  key={item.label}
                  className={`flex items-start justify-between gap-4 ${isNet ? 'border-t border-stone-200 pt-2 mt-1' : ''}`}
                >
                  <span className={`text-xs shrink-0 ${isNet ? 'font-bold text-stone-700' : 'text-stone-400'}`}>
                    {item.label}
                  </span>
                  <span className={`text-xs font-medium text-right max-w-[60%] ${
                    isFee ? 'text-red-400' : isNet ? 'font-black text-emerald-600' : 'text-stone-700'
                  }`}>
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Status Badge */}
      <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-xl shrink-0">
          <i className="ri-time-line text-blue-600"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-blue-800">في انتظار المراجعة</p>
          <p className="text-xs text-blue-600">سيتم مراجعة حملتك خلال 24-48 ساعة</p>
        </div>
      </div>

      {/* Platform Fee Notice */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-percent-line text-emerald-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-bold text-emerald-800 mb-1">رسوم المنصة 5%</p>
            <p className="text-xs text-emerald-700 leading-relaxed">
              تقتطع منصة هِبة رسوماً إدارية بنسبة <strong>5%</strong> من إجمالي التبرعات المحصّلة.
              يُخصم هذا المبلغ تلقائياً عند تحويل الأموال إلى حسابك البنكي.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Agreement */}
      <button
        type="button"
        onClick={() => onAgreeChange(!agreed)}
        className="flex items-start gap-3 cursor-pointer w-full text-right"
      >
        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${agreed ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300'}`}>
          {agreed && <i className="ri-check-line text-white text-xs"></i>}
        </div>
        <span className="text-sm text-stone-600 leading-relaxed">
          أوافق على{' '}
          <span className="text-emerald-600 font-medium">شروط وأحكام</span> منصة هِبة، وأقرّ بأن رسوم المنصة (5%) ستُخصم من إجمالي التبرعات.
        </span>
      </button>
    </div>
  );
};

export default Step4Review;
